import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, LogIn } from "lucide-react";

const PORTAL_URL = "https://portal.pivot1st.co.uk/";

const links = [
  { label: "Home", to: "/" },
  { label: "Classes", to: "/classes" },
  { label: "Timetable", to: "/timetable" },
  { label: "Pricing", to: "/pricing" },
  { label: "Blog", to: "/blog" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) => (to === "/" ? pathname === "/" : pathname.startsWith(to));

  return (
    <header
      className={`sticky top-0 z-50 bg-[#0A2540]/95 backdrop-blur-xl border-b border-[#FF7A1A]/20 transition-shadow duration-300 ${
        scrolled ? "shadow-lg shadow-black/20" : ""
      }`}
      data-testid="navbar"
    >
      <nav className="container mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Happy Tiddlers Swim School logo"
            className="h-14 w-14 object-contain group-hover:scale-105 transition-transform duration-300"
          />
          <span className="font-logo text-2xl md:text-[26px] leading-none text-white tracking-wide">
            Happy <span className="text-[#38BDF8]">Tiddlers</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-6">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              aria-current={isActive(l.to) ? "page" : undefined}
              className={`transition-colors duration-200 font-medium text-[15px] ${
                isActive(l.to) ? "text-[#38BDF8]" : "text-[#94A3B8] hover:text-[#38BDF8]"
              }`}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </Link>
          ))}
          <a
            href={PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[#94A3B8] hover:text-[#38BDF8] font-medium text-[15px] transition-colors duration-200"
            data-testid="nav-portal-link"
          >
            <LogIn size={16} /> Member Portal
          </a>
          <Link
            to="/book"
            className="bg-[#FF7A1A] text-white font-bold px-6 py-3 rounded-full shadow-[0_8px_20px_-6px_rgba(255,122,26,0.6)] hover:-translate-y-0.5 hover:shadow-[0_12px_24px_-6px_rgba(255,122,26,0.7)] transition-all duration-300"
            data-testid="nav-book-btn"
          >
            Book Now
          </Link>
        </div>

        <button
          className="lg:hidden text-white"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-[#0A2540] border-t border-[#FF7A1A]/20 px-6 py-6 flex flex-col gap-5" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={`font-medium ${isActive(l.to) ? "text-[#38BDF8]" : "text-[#94A3B8] hover:text-[#38BDF8]"}`}
            >
              {l.label}
            </Link>
          ))}
          <a
            href={PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="inline-flex items-center gap-2 font-medium text-[#94A3B8] hover:text-[#38BDF8]"
            data-testid="nav-portal-link-mobile"
          >
            <LogIn size={16} /> Member Portal
          </a>
          <Link
            to="/book"
            onClick={() => setOpen(false)}
            className="bg-[#FF7A1A] text-white font-bold px-6 py-3 rounded-full text-center"
          >
            Book Now
          </Link>
        </div>
      )}
    </header>
  );
}
